/**
 * Client-side env resolution (E8 §11 — `VITE_*` is non-secret by
 * construction; this app never depends on `@xdocs/config`'s Node env loader
 * per the F0d/F0e dependency direction, §3 — no server-only package reaches
 * the browser bundle).
 *
 * `VITE_WS_URL` isn't declared in the root `.env.example` yet (out of this
 * app's `apps/web/**` ownership boundary — flagged as an open issue); a
 * same-origin `/ws` derivation keeps local dev + preview deploys working
 * with zero config in the meantime.
 */

function deriveDevWsUrl(): string {
  if (typeof window === 'undefined') return 'ws://localhost:8080/ws';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // Vite dev server proxies /ws to the local @xdocs/server (documented in
  // apps/web/vite.config.ts); a same-origin path keeps this working whether
  // the app is served from Vite's dev server or a preview build.
  return `${protocol}//${window.location.host}/ws`;
}

export function resolveWsUrl(): string {
  const configured = import.meta.env.VITE_WS_URL;
  if (configured && configured.trim().length > 0) return configured;
  return deriveDevWsUrl();
}

export function resolveBuildVersion(): string {
  return import.meta.env.VITE_BUILD_VERSION ?? 'dev';
}

/**
 * Same-origin HTTP-scheme counterpart of `resolveWsUrl()`, for the plain
 * (non-upgrade) fetches the refresh-cookie lifecycle needs
 * (`auth/sessionCookieTransport.ts`: `?bootstrap=`/`?refresh=1`/`?logout=1`).
 * Unlike `uploadTransport.ts`/`vapidTransport.ts` (which own a DIFFERENT
 * REST path and so derive their URL independently), these ride the exact
 * SAME `/ws` path as query-param branches (`plugins/websocket.ts`), so this
 * just swaps `ws(s):` for `http(s):` on whatever `resolveWsUrl()` already
 * resolved — one override point (`VITE_WS_URL`) covers both, and it's
 * already correct in dev (proxy), e2e (`VITE_WS_URL=ws://localhost:8090/ws`),
 * and prod.
 */
export function resolveWsHttpUrl(): string {
  return resolveWsUrl().replace(/^ws(s?):\/\//, 'http$1://');
}

/**
 * Google OAuth 2.0 public client id (F12 path 1, `vite-env.d.ts`'s doc
 * comment) — `null` when unset (no Google app configured for this
 * environment), which `LoginPage`'s Google tab treats as "not available
 * here" rather than attempting a redirect that could only ever 503 at the
 * callback (`routes/oauth.ts`'s `google_oauth_not_configured`).
 */
export function resolveGoogleOauthClientId(): string | null {
  const configured = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
  return configured && configured.trim().length > 0 ? configured.trim() : null;
}
